import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import AMPageShell from './components/AMPageShell';
import { ASSETS } from './data/assetData';


const TIMELINE_START = 2017;
const TIMELINE_END = 2042;
const TOTAL_YEARS = TIMELINE_END - TIMELINE_START;

const SECTOR_COLOURS = {
  'Renewable Energy': '#0D9488',
  'Transport': '#3b4c4c',
  'Digital Infrastructure': '#8B5CF6',
  'Utilities': '#F59E0B',
};

function yearToPercent(year) {
  return ((year - TIMELINE_START) / TOTAL_YEARS) * 100;
}

function dateToYear(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr)?.getFullYear() + new Date(dateStr)?.getMonth() / 12;
}

function getConstructionDates(asset) {
  const start = asset?.keyOperationalDates?.find((d) => d?.label === 'Construction Start')?.date;
  const end = asset?.keyOperationalDates?.find((d) => d?.label === 'Expected COD' || d?.label === 'Expected Revenue Start')?.date
    || asset?.keyExpiryDates?.find((d) => d?.label === 'Construction Completion')?.date;
  return { start: start ? dateToYear(start) : null, end: end ? dateToYear(end) : null };
}

function getCODYear(asset) {
  const cod = asset?.keyOperationalDates?.find((d) => d?.label === 'COD' || d?.label === 'Expected COD' || d?.label === 'Expected Revenue Start')?.date;
  if (cod) return dateToYear(cod);
  return dateToYear(asset?.acquisitionDate);
}

const TODAY_YEAR = 2025 + 11 / 12;

function GanttRow({ asset, showEvents, onAssetClick }) {
  const [tooltip, setTooltip] = useState(null);
  const acqYear = dateToYear(asset?.acquisitionDate);
  const exitYear = asset?.exitProjectedYear;
  const codYear = getCODYear(asset);
  const construction = getConstructionDates(asset);
  const hasConstruction = construction?.start && construction?.end;

  const operationalStart = hasConstruction ? construction?.end : acqYear;
  const operationalEnd = exitYear || TIMELINE_END;

  const acqPct = yearToPercent(acqYear);
  const opStartPct = yearToPercent(operationalStart);
  const opEndPct = yearToPercent(operationalEnd);
  const exitPct = yearToPercent(exitYear || TIMELINE_END);

  const debtEvents = asset?.keyExpiryDates?.filter((d) =>
    d?.label?.toLowerCase()?.includes('debt') || d?.label?.toLowerCase()?.includes('loan')
  ) || [];
  const keyExpiry = asset?.keyExpiryDates?.filter((d) =>
    !d?.label?.toLowerCase()?.includes('debt') && !d?.label?.toLowerCase()?.includes('loan')
  ) || [];

  return (
    <div className="relative flex items-center h-14 border-b border-slate-100 group">
      {/* Asset label */}
      <div className="w-44 flex-shrink-0 pr-3 flex flex-col justify-center">
        <button
          onClick={() => onAssetClick(asset?.id)}
          className="text-xs font-semibold text-primary hover:underline text-left leading-tight"
        >
          {asset?.name}
        </button>
        <span
          className="text-xs px-1.5 py-0.5 rounded-full mt-0.5 inline-block w-fit"
          style={{ background: SECTOR_COLOURS?.[asset?.sector] + '20', color: SECTOR_COLOURS?.[asset?.sector], fontSize: '10px' }}
        >
          {asset?.sector?.split(' ')?.[0]}
        </span>
      </div>
      {/* Timeline area */}
      <div className="flex-1 relative h-8">
        {/* Construction bar */}
        {hasConstruction && showEvents?.construction && (
          <div
            className="absolute h-4 rounded top-0"
            style={{
              left: `${yearToPercent(construction?.start)}%`,
              width: `${yearToPercent(construction?.end) - yearToPercent(construction?.start)}%`,
              background: '#F59E0B',
              opacity: 0.85,
              top: '0px',
            }}
            title={`Construction: ${construction?.start?.toFixed(1)} – ${construction?.end?.toFixed(1)}`}
          />
        )}

        {/* Operational bar */}
        <div
          className="absolute h-4 rounded"
          style={{
            left: `${opStartPct}%`,
            width: `${Math.max(0, opEndPct - opStartPct - 2)}%`,
            background: '#0D9488',
            opacity: 0.8,
            top: '16px',
          }}
        />

        {/* Exit gradient zone */}
        {exitYear && (
          <div
            className="absolute h-4 rounded-r"
            style={{
              left: `${exitPct - 1.5}%`,
              width: '3%',
              background: 'linear-gradient(to right, transparent, #EF4444)',
              top: '16px',
            }}
          />
        )}

        {/* Acquisition diamond */}
        {showEvents?.acquisitions && (
          <div
            className="absolute cursor-pointer"
            style={{ left: `${acqPct}%`, top: '8px', transform: 'translateX(-50%)' }}
            onMouseEnter={(e) => setTooltip({ text: `Acquired: ${asset?.acquisitionDate?.slice(0, 7)}`, x: e?.clientX, y: e?.clientY })}
            onMouseLeave={() => setTooltip(null)}
          >
            <div className="w-3 h-3 rotate-45 bg-[#3b4c4c] border-2 border-white shadow" />
          </div>
        )}

        {/* Debt event markers */}
        {showEvents?.debtEvents && debtEvents?.map((ev, i) => {
          const yr = dateToYear(ev?.date);
          if (!yr || yr < TIMELINE_START || yr > TIMELINE_END) return null;
          return (
            <div
              key={i}
              className="absolute cursor-pointer"
              style={{ left: `${yearToPercent(yr)}%`, top: '6px', transform: 'translateX(-50%)' }}
              onMouseEnter={(e) => setTooltip({ text: `${ev?.label}: ${ev?.date?.slice(0, 7)}`, x: e?.clientX, y: e?.clientY })}
              onMouseLeave={() => setTooltip(null)}
            >
              <div className="w-2.5 h-2.5 rotate-45 bg-[#3b4c4c] border border-white" />
            </div>
          );
        })}

        {/* Key expiry markers */}
        {showEvents?.reviews && keyExpiry?.map((ev, i) => {
          const yr = dateToYear(ev?.date);
          if (!yr || yr < TIMELINE_START || yr > TIMELINE_END) return null;
          return (
            <div
              key={i}
              className="absolute cursor-pointer"
              style={{ left: `${yearToPercent(yr)}%`, top: '6px', transform: 'translateX(-50%)' }}
              onMouseEnter={(e) => setTooltip({ text: `${ev?.label}: ${ev?.date?.slice(0, 7)}`, x: e?.clientX, y: e?.clientY })}
              onMouseLeave={() => setTooltip(null)}
            >
              <div className="w-2.5 h-2.5 rounded-full bg-amber-400 border border-white" />
            </div>
          );
        })}

        {/* Today line */}
        <div
          className="absolute top-0 bottom-0 border-l-2 border-red-500 border-dashed"
          style={{ left: `${yearToPercent(TODAY_YEAR)}%`, height: '32px' }}
        />

        {tooltip && (
          <div
            className="fixed z-50 bg-slate-900 text-white text-xs px-2 py-1 rounded shadow-lg pointer-events-none"
            style={{ left: tooltip?.x + 12, top: tooltip?.y - 28 }}
          >
            {tooltip?.text}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AssetLifecycle({ selectedAssets, showToast }) {
  const assets = selectedAssets || ASSETS;
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('all');
  const [zoom, setZoom] = useState('full');
  const [showEvents, setShowEvents] = useState({
    acquisitions: true,
    construction: true,
    debtEvents: true,
    distributions: true,
    exits: true,
    reviews: true,
  });

  const today = new Date();
  const todayStr = today?.toISOString()?.slice(0, 10);
  const nextYear = new Date(today?.getTime() + 365 * 24 * 60 * 60 * 1000)?.toISOString()?.slice(0, 10);

  const upcomingEvents = assets?.flatMap((a) => [
    ...a?.keyExpiryDates?.map((ev) => ({ ...ev, assetName: a?.name, assetId: a?.id, type: 'expiry' })),
    ...a?.keyOperationalDates?.map((ev) => ({ ...ev, assetName: a?.name, assetId: a?.id, type: 'operational' })),
    { label: 'Next Review', date: a?.nextReviewDate, assetName: a?.name, assetId: a?.id, type: 'review' },
  ])?.filter((ev) => ev?.date >= todayStr && ev?.date <= nextYear)?.sort((a, b) => a?.date?.localeCompare(b?.date))?.slice(0, 8);

  const approachingExit = assets
    ?.filter((a) => a?.exitProjectedYear && a?.exitProjectedYear <= 2030)
    ?.sort((a, b) => a?.exitProjectedYear - b?.exitProjectedYear);

  const debtEvents = assets?.flatMap((a) =>
    a?.keyExpiryDates
      ?.filter((ev) => ev?.label?.toLowerCase()?.includes('debt') || ev?.label?.toLowerCase()?.includes('loan'))
      ?.map((ev) => ({ ...ev, assetName: a?.name, assetId: a?.id }))
  )?.sort((a, b) => a?.date?.localeCompare(b?.date));

  // Vintage bar chart data
  const vintageData = Array.from({ length: 7 }, (_, i) => {
    const year = 2017 + i;
    const yearAssets = assets?.filter((a) => new Date(a?.acquisitionDate)?.getFullYear() === year);
    const bySector = {};
    yearAssets?.forEach((a) => {
      bySector[a?.sector] = (bySector?.[a?.sector] || 0) + (a?.fundExposure || 0);
    });
    return { year: String(year), ...bySector, total: yearAssets?.reduce((s, a) => s + (a?.fundExposure || 0), 0) };
  })?.filter((d) => d?.total > 0);

  const xAxisYears = Array.from({ length: TOTAL_YEARS + 1 }, (_, i) => TIMELINE_START + i)?.filter((y) => y % 5 === 0);

  const toggleEvent = (key) => setShowEvents((prev) => ({ ...prev, [key]: !prev?.[key] }));

  return (
    <AMPageShell activeTab="/asset-management/asset-lifecycle">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.375rem' }}>Asset Lifecycle Timeline</h2>
          <p className="font-caption text-muted-foreground text-sm mt-0.5">Acquisition, construction, operations, and exit across all assets — Sample data</p>
        </div>

        {/* Controls */}
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <span className="font-caption text-xs text-muted-foreground font-medium uppercase tracking-wide">View</span>
              <div className="flex rounded-md border border-border overflow-hidden">
                {['all', 'sector', 'country', 'fund']?.map((v) => (
                  <button
                    key={v}
                    onClick={() => setViewMode(v)}
                    className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                      viewMode === v ? 'bg-primary text-white' : 'bg-background text-muted-foreground hover:bg-slate-50'
                    }`}
                  >
                    {v === 'all' ? 'All Assets' : `By ${v?.charAt(0)?.toUpperCase() + v?.slice(1)}`}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-caption text-xs text-muted-foreground font-medium uppercase tracking-wide">Zoom</span>
              <div className="flex rounded-md border border-border overflow-hidden">
                {['5yr', '10yr', '15yr', 'full']?.map((v) => (
                  <button
                    key={v}
                    onClick={() => setZoom(v)}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      zoom === v ? 'bg-primary text-white' : 'bg-background text-muted-foreground hover:bg-slate-50'
                    }`}
                  >
                    {v === 'full' ? 'Full' : v?.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-caption text-xs text-muted-foreground font-medium uppercase tracking-wide">Show</span>
              {Object.entries(showEvents)?.map(([key, val]) => (
                <button
                  key={key}
                  onClick={() => toggleEvent(key)}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors capitalize ${
                    val ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-background border-border text-muted-foreground'
                  }`}
                >
                  {key === 'debtEvents' ? 'Debt Events' : key?.charAt(0)?.toUpperCase() + key?.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main layout: Timeline + Sidebar */}
        <div className="flex gap-5">
          {/* Gantt Timeline */}
          <div className="flex-1 bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <h3 className="font-heading font-semibold text-foreground text-sm">Lifecycle Timeline (2017–2042)</h3>
            </div>
            <div className="overflow-x-auto">
              <div style={{ minWidth: '700px' }}>
                {/* Year axis */}
                <div className="flex items-center h-8 border-b border-slate-100 bg-slate-50">
                  <div className="w-44 flex-shrink-0" />
                  <div className="flex-1 relative">
                    {xAxisYears?.map((yr) => (
                      <div
                        key={yr}
                        className="absolute text-xs text-muted-foreground"
                        style={{ left: `${yearToPercent(yr)}%`, transform: 'translateX(-50%)' }}
                      >
                        {yr}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Asset rows */}
                <div className="px-4">
                  {assets?.map((asset) => (
                    <GanttRow
                      key={asset?.id}
                      asset={asset}
                      showEvents={showEvents}
                      onAssetClick={(id) => navigate(`/asset-management/asset-detail/${id}`)}
                    />
                  ))}
                </div>
                {/* Legend */}
                <div className="flex flex-wrap gap-4 px-4 py-3 border-t border-slate-100 bg-slate-50">
                  <div className="flex items-center gap-1.5"><div className="w-3 h-3 rotate-45 bg-[#3b4c4c]" /><span className="text-xs text-muted-foreground">Acquisition</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-8 h-3 rounded bg-amber-400" /><span className="text-xs text-muted-foreground">Construction</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-8 h-3 rounded bg-teal-500" /><span className="text-xs text-muted-foreground">Operational</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded" style={{ background: 'linear-gradient(to right, transparent, #EF4444)' }} /><span className="text-xs text-muted-foreground">Exit Zone</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-0.5 h-4 border-l-2 border-red-500 border-dashed" /><span className="text-xs text-muted-foreground">Today</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Panel */}
          <div className="w-72 flex-shrink-0 space-y-4">
            {/* Upcoming Events */}
            <div className="bg-card border border-border rounded-lg p-4">
              <h4 className="font-heading font-semibold text-foreground text-sm mb-3">Upcoming Events (12 months)</h4>
              <div className="space-y-2">
                {upcomingEvents?.length === 0 && <p className="text-xs text-muted-foreground">No events in next 12 months.</p>}
                {upcomingEvents?.map((ev, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                      ev?.type === 'review' ? 'bg-green-500' : ev?.type === 'expiry' ? 'bg-amber-500' : 'bg-[rgba(59,76,76,0.07)]0'
                    }`} />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{ev?.label}</p>
                      <p className="text-xs text-muted-foreground">{ev?.assetName?.split(' ')?.slice(0, 2)?.join(' ')} · {ev?.date?.slice(0, 7)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Approaching Exit */}
            <div className="bg-card border border-border rounded-lg p-4">
              <h4 className="font-heading font-semibold text-foreground text-sm mb-3">Approaching Exit (5 yrs)</h4>
              <div className="space-y-2">
                {approachingExit?.length === 0 && <p className="text-xs text-muted-foreground">No assets approaching exit.</p>}
                {approachingExit?.map((a) => (
                  <div key={a?.id} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{a?.name}</p>
                      <p className="text-xs text-muted-foreground">Exit {a?.exitProjectedYear} · {a?.multiple?.toFixed(1)}× MOIC</p>
                    </div>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                      a?.watchlistStatus === 'Exit case' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {a?.watchlistStatus === 'Exit case' ? 'Exit' : 'Active'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Debt Events */}
            <div className="bg-card border border-border rounded-lg p-4">
              <h4 className="font-heading font-semibold text-foreground text-sm mb-3">Refinancing / Debt Events</h4>
              <div className="space-y-2">
                {debtEvents?.length === 0 && <p className="text-xs text-muted-foreground">No debt events found.</p>}
                {debtEvents?.slice(0, 5)?.map((ev, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="mt-0.5 w-2 h-2 rotate-45 bg-[#3b4c4c] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground">{ev?.label}</p>
                      <p className="text-xs text-muted-foreground">{ev?.assetName?.split(' ')?.slice(0, 2)?.join(' ')} · {ev?.date?.slice(0, 7)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Vintage Bar Chart */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="font-heading font-semibold text-foreground text-base mb-1">Fund Exposure by Vintage Year</h3>
          <p className="font-caption text-xs text-muted-foreground mb-4">Total fund exposure (£m) by acquisition year, stacked by sector</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={vintageData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#64748B' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={(v) => `£${v}m`} />
              <Tooltip formatter={(v, name) => [`£${v?.toFixed(1)}m`, name]} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              {Object.keys(SECTOR_COLOURS)?.map((sector) => (
                <Bar key={sector} dataKey={sector} stackId="a" fill={SECTOR_COLOURS?.[sector]} radius={[0, 0, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </AMPageShell>
  );
}
